<script setup lang="ts">
import type { Entry } from '../internal/types.ts'
import MoneyAmount from '@/app/components/MoneyAmount.vue'
import { EmptyState, ErrorState, KindIcon, NavIcon, Skeleton } from '@taler/ui'
import {
  type CurrencyCode,
  formatDate,
  type Locale,
  type Money,
  negateMoney,
  signOfMoney,
} from '@taler/domain'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { RouterLink } from 'vue-router'
import { plainAmount, signTone, useAccountingView } from '@/app/accounting-view'
import { type AccountLabel, FX_RESIDUAL_CODE } from '../internal/account-labels.ts'
import { movedAmount, ownAmount, postingFigures } from '../internal/own-posting.ts'

type Posting = Entry['postings'][number]

const props = defineProps<{
  entries: readonly Entry[]
  /** The accounts whose postings this list's own figures sum: the account, or its subtree for a group. */
  accountIds: ReadonlySet<string>
  accountClass: string | undefined
  labels: ReadonlyMap<string, AccountLabel>
  displayCurrency: CurrencyCode
  locale: Locale
  isLoading: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  error: Error | null
  retrying?: boolean | undefined
}>()

const emit = defineEmits<{ loadMore: []; retry: [] }>()

const { t } = useI18n()
const { accountingView } = useAccountingView()

function title(entry: Entry): string {
  return entry.payee ?? (entry.memo || t('transactions.noDescription'))
}

function leafOf(accountId: string): string {
  return props.labels.get(accountId)?.leaf ?? accountId
}

function isResidual(posting: Posting): boolean {
  return props.labels.get(posting.accountId)?.code === FX_RESIDUAL_CODE
}

function legsOf(entry: Entry): Posting[] {
  return entry.postings.filter((posting) => !isResidual(posting))
}

/** Money left one of this list's accounts and arrived in another, with no real leg outside them. */
function isInternal(entry: Entry): boolean {
  const legs = legsOf(entry)
  const accounts = new Set(legs.map((posting) => posting.accountId))
  return (
    accounts.size > 1 &&
    [...accounts].every((accountId) => props.accountIds.has(accountId)) &&
    legs.some((posting) => signOfMoney(posting.amount) < 0) &&
    legs.some((posting) => signOfMoney(posting.amount) > 0)
  )
}

function namesOf(legs: readonly Posting[], nameOf: (accountId: string) => string): string {
  return [...new Set(legs.map((posting) => posting.accountId))].map(nameOf).join(', ')
}

/** Every sub-account named, sources then destinations; a movement over more than three legs is counted instead. */
function internalLabel(entry: Entry): string {
  const legs = legsOf(entry)
  if (legs.length > 3) {
    return t('accountDetail.internalBetween', {
      count: new Set(legs.map((posting) => posting.accountId)).size,
    })
  }
  const from = legs.filter((posting) => signOfMoney(posting.amount) < 0)
  const to = legs.filter((posting) => signOfMoney(posting.amount) > 0)
  const leafCounts = new Map<string, number>()
  for (const accountId of new Set(legs.map((posting) => posting.accountId))) {
    leafCounts.set(leafOf(accountId), (leafCounts.get(leafOf(accountId)) ?? 0) + 1)
  }
  const nameOf = (accountId: string): string => {
    const leaf = leafOf(accountId)
    const path = props.labels.get(accountId)?.path ?? []
    return (leafCounts.get(leaf) ?? 0) > 1 && path.length > 1 ? path.slice(-2).join(' › ') : leaf
  }
  return t('accountDetail.internalMovement', { from: namesOf(from, nameOf), to: namesOf(to, nameOf) })
}

/** The other side of the entry, named once; more legs are counted rather than listed. */
function counterpart(entry: Entry): string {
  if (isInternal(entry)) return internalLabel(entry)
  const others = entry.postings.filter((posting) => !props.accountIds.has(posting.accountId))
  const first = others[0]
  if (first === undefined) return '—'
  const leaf = leafOf(first.accountId)
  return others.length > 1 ? `${leaf} ${t('accountDetail.counterMore', { count: others.length - 1 })}` : leaf
}

/** A transfer, or a movement inside the list's accounts, keeps the neutral tone; every other row takes the tone of the sign it shows. */
function rowTone(
  entry: Entry,
  amount: Money,
  internal: boolean,
): 'transfer' | 'positive' | 'negative' | undefined {
  return entry.kind === 'TRANSFER' || internal ? 'transfer' : signTone(amount)
}

function kindLabel(entry: Entry): string {
  return entry.kind === undefined ? t('transactions.noKind') : t(`transactions.${entry.kind.toLowerCase()}`)
}

const rows = computed(() =>
  props.entries.map((entry) => {
    const internal = isInternal(entry)
    if (internal) {
      const moved = movedAmount(
        legsOf(entry).filter((posting) => signOfMoney(posting.amount) < 0),
        props.displayCurrency,
      )
      const differences = postingFigures(entry.postings.filter(isResidual), props.displayCurrency)
        .figures.map((figure) => plainAmount(negateMoney(figure), props.accountClass, accountingView.value))
        .filter((figure) => signOfMoney(figure) !== 0)
      return { entry, internal, amounts: moved.figures, rateMissing: moved.rateMissing, differences }
    }
    const own = ownAmount(entry, props.accountIds, props.displayCurrency)
    return {
      entry,
      internal,
      amounts: own.figures.map((figure) => plainAmount(figure, props.accountClass, accountingView.value)),
      rateMissing: own.rateMissing,
      differences: [] as readonly Money[],
    }
  }),
)
</script>

<template>
  <div class="flex flex-col gap-[var(--space-group)]">
    <ul v-if="isLoading" aria-hidden="true" class="flex flex-col gap-[var(--space-inline)]">
      <li v-for="index in 5" :key="index"><Skeleton height="2.5rem" /></li>
    </ul>
    <p v-if="isLoading" class="sr-only" role="status">{{ t('accountDetail.loading') }}</p>

    <ErrorState
      v-else-if="error"
      :title="t('accountDetail.entriesErrorTitle')"
      :body="t('accountDetail.entriesErrorBody')"
      :retry-label="t('accountDetail.retry')"
      :retrying="retrying"
      @retry="emit('retry')"
    />

    <EmptyState
      v-else-if="entries.length === 0"
      :title="t('accountDetail.emptyTitle')"
      :body="t('accountDetail.emptyBody')"
    >
      <template #icon><NavIcon name="transactions" /></template>
    </EmptyState>

    <template v-else>
      <ul
        :aria-label="t('accountDetail.entriesTitle')"
        class="flex flex-col divide-y divide-[color:var(--color-border)] rounded-[var(--radius-surface)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)]"
      >
        <li
          v-for="{ entry, internal, amounts, rateMissing, differences } in rows"
          :key="entry.id"
          class="relative flex min-h-[var(--density-row)] items-center gap-[var(--space-cell)] px-[var(--space-cell)] py-[var(--space-inline)] hover:bg-[color:var(--color-surface-raised)] focus-within:bg-[color:var(--color-surface-raised)]"
        >
          <span
            class="w-[5.5rem] shrink-0 text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
          >
            {{ formatDate(entry.date, locale, 'short') }}
          </span>
          <span
            class="flex h-7 w-7 shrink-0 items-center justify-center rounded-[var(--radius-control)] bg-[color:var(--color-surface-sunken)]"
          >
            <KindIcon :kind="entry.kind" :label="kindLabel(entry)" />
          </span>
          <div class="min-w-0 flex-1">
            <RouterLink
              :to="{ name: 'transactions', query: { entry: entry.id } }"
              class="block truncate text-[length:var(--text-step-3)] font-medium text-[color:var(--color-text)] after:absolute after:inset-0 outline-none focus-visible:underline"
            >
              {{ title(entry) }}
            </RouterLink>
            <p class="truncate text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
              {{ counterpart(entry) }}
            </p>
            <p
              v-if="internal"
              class="truncate text-[length:var(--text-step-1)] italic text-[color:var(--color-text-muted)]"
            >
              {{
                differences.length > 0
                  ? t('accountDetail.internalNoteExchange')
                  : t('accountDetail.internalNote')
              }}
            </p>
          </div>
          <span class="flex w-[13rem] shrink-0 flex-col items-end">
            <MoneyAmount
              v-for="(amount, index) in amounts"
              :key="index"
              :value="amount"
              :sign="!internal"
              :tone="rowTone(entry, amount, internal)"
            />
            <p
              v-if="differences.length > 0"
              class="text-right text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]"
            >
              {{ t('accountDetail.exchangeDifference') }}
              <MoneyAmount v-for="(difference, index) in differences" :key="index" :value="difference" sign />
            </p>
            <p
              v-if="rateMissing"
              class="text-right text-[length:var(--text-step-1)] italic text-[color:var(--color-text-muted)]"
            >
              {{ t('transactions.noRateForDate') }}
            </p>
          </span>
        </li>
      </ul>

      <div class="flex justify-center">
        <button
          v-if="hasNextPage"
          type="button"
          :disabled="isFetchingNextPage"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-[var(--space-cell)] py-[var(--space-inline)] text-[color:var(--color-text)] disabled:opacity-60"
          @click="emit('loadMore')"
        >
          {{ isFetchingNextPage ? t('accountDetail.loadingMore') : t('accountDetail.loadMore') }}
        </button>
        <p v-else class="text-[length:var(--text-step-1)] text-[color:var(--color-text-muted)]">
          {{ t('accountDetail.endOfList') }}
        </p>
      </div>
    </template>
  </div>
</template>
